/**
 * AccessTokensPanel
 *
 * Mint, review and revoke personal access tokens — the credential that lets
 * someone drive the platform from Claude Code as themselves.
 *
 * Two things this screen has to get right, because both are irreversible in
 * different directions:
 *
 *   1. The raw token is shown ONCE. If the person closes the dialog without
 *      copying it, it is gone and they must mint another. So the reveal is a
 *      modal they must dismiss deliberately, not a toast that fades.
 *   2. Revoking is instant and total. It is presented as a confirmation, not
 *      a one-click affordance sitting next to "copy".
 *
 * Layout: one `.security-card` in the Settings page's own vocabulary — the
 * same `.security-item` rows, icon tiles and dividers the Security, Preferences
 * and Legal sections are built from — so the section reads as one design with
 * its neighbours rather than a table dropped into the page.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Checkbox, Input, InputNumber, Modal, Popconfirm,
  Space, Tooltip, Typography, message,
} from 'antd';
import { CopyOutlined, KeyOutlined, PlusOutlined } from '@ant-design/icons';

import { useLanguage } from '@hooks/useLanguage';
import { useAuth } from '@hooks/useAuth';
import { activeLocale } from '@/utils/locale';
import DeveloperConnectGuide, { copyText } from './DeveloperConnectGuide';
import './DeveloperConnectGuide.css';
import './AccessTokensPanel.css';
import accessTokenService, {
  AccessToken, AppIdentity, CreatedAccessToken, TokenScope,
} from '../services/accessTokenService';

const ALL_SCOPES: TokenScope[] = ['read', 'write', 'chat'];
/** Mirrors MAX_TTL_DAYS / SUPER_ADMIN_MAX_TTL_DAYS in personal_access_token.py. */
const MAX_TTL_DAYS = 365;
const SUPER_ADMIN_MAX_TTL_DAYS = 30;

export interface AccessTokenRowProps {
  token: AccessToken;
  onRevoke: (tokenId: string) => void;
}

/** One token as a `.security-item` row: name, scope chips, usage line, Revoke. */
function AccessTokenRow({ token, onRevoke }: AccessTokenRowProps) {
  const { t } = useLanguage();

  // activeLocale(), not the browser default — a Hebrew-speaking person on an
  // English-locale machine must still see Hebrew dates.
  const formatDate = (value: string) => new Date(value).toLocaleDateString(activeLocale());

  const lastUsed = token.last_used_at
    ? t('accessTokens.lastUsedOn', { date: formatDate(token.last_used_at) })
    : t('accessTokens.neverUsed');
  const expires = token.expires_at
    ? t('accessTokens.expiresOn', { date: formatDate(token.expires_at) })
    : null;

  return (
    <div className="security-item pat-row" data-testid={`access-token-${token.token_id}`}>
      <div className="security-content">
        <h3 className="security-title pat-row__title">
          <span dir="auto">{token.token_name}</span>
          <span className="pat-row__prefix" dir="ltr">{token.token_prefix}…</span>
        </h3>
        <div className="pat-row__meta">
          <span className="pat-chips">
            {(token.scopes || []).map((s) => (
              <span key={s} className={`pat-chip pat-chip--${s}`}>
                {t(`accessTokens.scope.${s}`) || s}
              </span>
            ))}
          </span>
          <p className="security-description pat-row__usage">
            <span>{lastUsed}</span>
            {expires && <span className="pat-row__sep" aria-hidden="true">·</span>}
            {expires && <span>{expires}</span>}
          </p>
        </div>
      </div>
      <Popconfirm
        title={t('accessTokens.revokeConfirmTitle')}
        description={t('accessTokens.revokeConfirmBody')}
        okText={t('accessTokens.revoke')}
        okButtonProps={{ danger: true }}
        cancelText={t('common.cancel')}
        onConfirm={() => onRevoke(token.token_id)}
      >
        <button type="button" className="pat-revoke" data-testid={`access-token-revoke-${token.token_id}`}>
          {t('accessTokens.revoke')}
        </button>
      </Popconfirm>
    </div>
  );
}

export default function AccessTokensPanel() {
  const { t } = useLanguage();
  const { isSuperAdmin } = useAuth();
  // A super admin's token carries platform-wide authority: it may not outlive a month.
  const maxTtl = isSuperAdmin ? SUPER_ADMIN_MAX_TTL_DAYS : MAX_TTL_DAYS;
  const defaultTtl = Math.min(90, maxTtl);

  const [tokens, setTokens] = useState<AccessToken[]>([]);
  // What this person calls the platform, as the server resolves it.
  const [app, setApp] = useState<AppIdentity | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<TokenScope[]>(['read']);
  const [ttlDays, setTtlDays] = useState<number>(defaultTtl);

  // Held only while the reveal modal is open, then dropped.
  const [revealed, setRevealed] = useState<CreatedAccessToken | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const listed = await accessTokenService.list();
      setTokens(listed.tokens);
      setApp(listed.app);
    } catch {
      message.error(t('accessTokens.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!name.trim()) {
      message.warning(t('accessTokens.nameRequired'));
      return;
    }
    setCreating(true);
    try {
      const created = await accessTokenService.create({
        name: name.trim(), scopes, ttl_days: ttlDays,
      });
      setRevealed(created);
      setFormOpen(false);
      setName('');
      setScopes(['read']);
      setTtlDays(defaultTtl);
      await load();
    } catch (err: any) {
      message.error(
        err?.response?.data?.error
        || t('accessTokens.createFailed'),
      );
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (tokenId: string) => {
    try {
      await accessTokenService.revoke(tokenId);
      message.success(t('accessTokens.revoked'));
      await load();
    } catch {
      message.error(t('accessTokens.revokeFailed'));
    }
  };

  return (
    <>
      <div className="security-card pat-card" data-testid="access-tokens-panel">
        {/* ── Header row: what this is, and the one action ──────────────── */}
        <div className="security-item">
          <div className="security-icon">
            <KeyOutlined />
          </div>
          <div className="security-content">
            <h3 className="security-title">{t('accessTokens.sectionTitle')}</h3>
            <p className="security-description">{t('accessTokens.sectionSubtitle')}</p>
          </div>
          <button
            type="button"
            className="pat-action"
            onClick={() => setFormOpen(true)}
            data-testid="access-token-create"
          >
            <PlusOutlined />
            <span>{t('accessTokens.create')}</span>
          </button>
        </div>

        <div className="security-divider" />

        {/* ── Tokens, one row each ──────────────────────────────────────── */}
        <div className="pat-tokens" data-testid="access-token-list" aria-busy={loading}>
          {tokens.map((token) => (
            <AccessTokenRow key={token.token_id} token={token} onRevoke={handleRevoke} />
          ))}
          {!loading && tokens.length === 0 && (
            <div className="security-item pat-row pat-row--empty" data-testid="access-token-empty">
              <p className="security-description">{t('accessTokens.empty')}</p>
            </div>
          )}
        </div>

        <div className="security-divider" />

        {/* ── Connect a tool ────────────────────────────────────────────── */}
        {app && <DeveloperConnectGuide app={app} />}
      </div>

      {/* ── Create ─────────────────────────────────────────────────────── */}
      <Modal
        open={formOpen}
        title={t('accessTokens.createTitle')}
        okText={t('accessTokens.create')}
        cancelText={t('common.cancel')}
        confirmLoading={creating}
        onOk={handleCreate}
        onCancel={() => setFormOpen(false)}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Typography.Text strong>
              {t('accessTokens.nameLabel')}
            </Typography.Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('accessTokens.namePlaceholder')}
              maxLength={100}
              className="pat-panel__field"
            />
          </div>

          <div>
            <Typography.Text strong>
              {t('accessTokens.scopesLabel')}
            </Typography.Text>
            <Checkbox.Group
              value={scopes}
              onChange={(v) => setScopes(v as TokenScope[])}
              className="pat-panel__scopes"
            >
              <Space direction="vertical" size={4}>
                {ALL_SCOPES.map((s) => (
                  <Checkbox key={s} value={s}>
                    <Typography.Text strong>
                      {t(`accessTokens.scope.${s}`) || s}
                    </Typography.Text>
                    <Typography.Text type="secondary" className="pat-panel__scope-help">
                      {t(`accessTokens.scopeHelp.${s}`)}
                    </Typography.Text>
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          </div>

          <div>
            <Typography.Text strong>
              {t('accessTokens.ttlLabel')}
            </Typography.Text>
            <Tooltip title={t('accessTokens.ttlHelp', { max: maxTtl })}>
              <InputNumber
                min={1}
                max={maxTtl}
                value={ttlDays}
                onChange={(v) => setTtlDays(Math.min(Number(v) || defaultTtl, maxTtl))}
                className="pat-panel__ttl"
              />
            </Tooltip>
          </div>
        </Space>
      </Modal>

      {/* ── Reveal, once ───────────────────────────────────────────────── */}
      <Modal
        open={!!revealed}
        title={
          <Space>
            <KeyOutlined />
            {t('accessTokens.revealTitle')}
          </Space>
        }
        onCancel={() => setRevealed(null)}
        onOk={() => setRevealed(null)}
        okText={t('accessTokens.revealDone')}
        cancelButtonProps={{ style: { display: 'none' } }}
        // Deliberately not dismissible by clicking away: losing this means
        // minting a new one.
        maskClosable={false}
        width={680}
      >
        <Alert
          type="warning"
          showIcon
          className="pat-panel__warning"
          message={
            t('accessTokens.revealWarning')
          }
        />
        <Input.TextArea
          readOnly
          value={revealed?.token || ''}
          autoSize={{ minRows: 2, maxRows: 4 }}
          onFocus={(e) => e.currentTarget.select()}
          className="pat-panel__token"
          dir="ltr"
        />
        <Button
          icon={<CopyOutlined />}
          className="pat-panel__copy"
          onClick={() => revealed && copyText(revealed.token, t)}
        >
          {t('accessTokens.copy')}
        </Button>
        {/* The only moment the full token exists: hand over the ready-made setup too. */}
        {revealed && (
          <div className="security-card pat-card pat-card--in-modal">
            {app && <DeveloperConnectGuide app={app} token={revealed.token} />}
          </div>
        )}
      </Modal>
    </>
  );
}
